import { DateTime } from 'luxon';

import { ClientSummaryDto } from '../../ClientSummaryDto';
import { SignedDocument } from '../../document/SignedDocument';

import { ManifestSource } from './ManifestSource';
import { ManifestStatus } from './ManifestStatus';

export interface ManifestCarrier {
  name?: string;
  scac?: string;
}

export interface ManifestPickupLocation {
  locationId?: number;
  locationName?: string;
  code?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  timeZone?: string;
}

/**
 * Mirrors the backend `ManifestDto`
 * (com.rygen.corsair.domain.shipment.dto.parcelmanifest.ManifestDto).
 */
export class Manifest {
  id?: number;
  domain?: ClientSummaryDto;
  manifestNumber?: string;
  carrier?: ManifestCarrier;
  pickupLocation?: ManifestPickupLocation;
  manifestDate?: Date;
  status?: ManifestStatus;
  source?: ManifestSource;
  shipmentIds: number[] = [];
  errors: string[] = [];
  documents: SignedDocument[] = [];

  constructor(opts?: Partial<Manifest>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.domain != null) {
      this.domain = new ClientSummaryDto(opts.domain);
    }
    if (opts?.manifestNumber != null) {
      this.manifestNumber = opts.manifestNumber;
    }
    if (opts?.carrier != null) {
      this.carrier = { ...opts.carrier };
    }
    if (opts?.pickupLocation != null) {
      this.pickupLocation = { ...opts.pickupLocation };
    }
    if (opts?.manifestDate != null) {
      this.manifestDate = opts.manifestDate;
    }
    if (opts?.status != null) {
      this.status = opts.status;
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.shipmentIds != null) {
      this.shipmentIds = [...opts.shipmentIds];
    }
    if (opts?.errors != null) {
      this.errors = [...opts.errors];
    }
    if (opts?.documents != null) {
      this.documents = opts.documents.map((doc) => new SignedDocument(doc));
    }
  }

  get client(): ClientSummaryDto | undefined {
    return this.domain;
  }

  get shipmentCount(): number {
    return this.shipmentIds?.length ?? 0;
  }

  get hasDocument(): boolean {
    return this.documents.length > 0;
  }

  /**
   * The calendar day this manifest is for, as a local-zone start of day.
   *
   * `manifestDate` is midnight of the chosen day in the pickup location's
   * timezone, so the day must be read in that zone; reading it in the viewer's
   * zone shifts it by a day for viewers west of the pickup location.
   */
  get manifestDay(): DateTime | undefined {
    if (this.manifestDate == null) {
      return undefined;
    }
    const raw = this.manifestDate as unknown as string | Date;
    const dt =
      typeof raw === 'string'
        ? DateTime.fromISO(raw, { setZone: true })
        : DateTime.fromJSDate(raw);
    const timeZone = this.pickupLocation?.timeZone;
    const zoned = timeZone ? dt.setZone(timeZone) : dt;
    return DateTime.local(zoned.year, zoned.month, zoned.day);
  }
}
